(function () {
  var CONFIG_ELEMENT_ID = 'et-sentry-config';
  var THEME_ASSET_PATH_PATTERN = /\/cdn\/shop\/t\/\d+\/assets\//i;
  var NON_THEME_SAME_ORIGIN_PATH_PATTERNS = [
    /\/cdn\/shopifycloud\//i,
    /\/web-pixels@/i,
    /\/cdn\/wpm\//i,
    /\/extensions\//i
  ];
  // Tokens that uniquely identify Shopify's platform-injected analytics
  // bootstrap (Web Pixels Manager / Trekkie). Shopify renders this code as an
  // inline <script> in the page document via content_for_header, so its stack
  // frames share the page URL as their filename and would otherwise be
  // misclassified as theme-owned inline scripts. None of these tokens appear in
  // first-party theme source, so a frame referencing them is never ours.
  var SHOPIFY_INJECTED_SOURCE_TOKENS = [
    'webPixelsManager',
    '__TREKKIE_SHIM_QUEUE',
    'web-pixels-manager'
  ];
  // Signature of a frame whose "source" is an HTML document rather than
  // JavaScript. In-app browsers (e.g. Instagram) inject or rewrite a <script>
  // whose src resolves to the page URL itself, so the engine fetches the page's
  // own markup and parses it as JS, throwing `SyntaxError: Unexpected token
  // '<'`. The failing frame then carries the same-origin page URL as its
  // filename -- indistinguishable from a real inline theme script by path alone
  // -- but its source line is the document's opening markup, which no
  // first-party theme JavaScript ever is. (SHOPIFY-37V)
  var HTML_DOCUMENT_SOURCE_PATTERN = /^\s*(?:<!doctype\s+html|<html[\s>]|<head[\s>])/i;
  // High-precision automated-traffic signals. Headless engines and scrapers
  // hammer the storefront, choke on an inline <script> (parse-time
  // SyntaxError), and -- because the session is flagged crashed before
  // beforeSend can run and there is no beforeSendSession hook -- drag the
  // crash-free SESSION rate down without representing any real shopper. We bias
  // toward false negatives (miss a bot) over false positives (blind a real
  // user), so every token here is one a genuine shopper effectively never trips.
  var BOT_UA_PATTERN =
    /crawl|spider|slurp|headless|phantomjs|prerender|lighthouse|pagespeed|gtmetrix|bingpreview|puppeteer|playwright|selenium/i;
  // localStorage key for the persistent anonymous shopper id used as the Sentry
  // user when no customer is logged in.
  var ANON_ID_STORAGE_KEY = 'et_sentry_anonymous_id';
  var state = {
    config: null,
    initialized: false
  };

  function createNoopObservability() {
    return {
      captureException: function () { return null; },
      captureMessage: function () { return null; },
      startSpan: function (_context, callback) {
        if (typeof callback === 'function') return callback(null);
        return null;
      },
      startSpanManual: function (_context, callback) {
        if (typeof callback === 'function') return callback(null);
        return null;
      }
    };
  }

  function freezeValue(value) {
    if (typeof Object.freeze === 'function') return Object.freeze(value);
    return value;
  }

  function ensureObservability() {
    if (!window.etObservability || typeof window.etObservability !== 'object') {
      window.etObservability = freezeValue(createNoopObservability());
    }
  }

  function readConfigElement() {
    var configElement = document.getElementById(CONFIG_ELEMENT_ID);
    if (!configElement) return {};

    try {
      return JSON.parse(configElement.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function buildConfig() {
    var baseConfig = readConfigElement();
    var overrideConfig = window.etSentryConfig;

    if (!overrideConfig || typeof overrideConfig !== 'object') {
      overrideConfig = {};
    }

    state.config = Object.assign({}, baseConfig, overrideConfig);
    window.etSentryConfig = state.config;
    return state.config;
  }

  function parseSampleRate(rawValue, fallback) {
    var parsedValue = Number(rawValue);

    if (!Number.isFinite(parsedValue)) parsedValue = fallback;
    if (parsedValue < 0) return 0;
    if (parsedValue > 1) return 1;
    return parsedValue;
  }

  function parseBoolean(rawValue, fallback) {
    if (typeof rawValue === 'boolean') return rawValue;
    if (typeof rawValue === 'number') return rawValue !== 0;
    if (typeof rawValue === 'string') {
      var normalized = rawValue.trim().toLowerCase();

      if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
      if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
    }

    return fallback;
  }

  function isLikelyBot() {
    var nav = window.navigator || {};

    // navigator.webdriver is set true by Selenium/Playwright/Puppeteer and other
    // WebDriver-controlled browsers; genuine shoppers leave it false/undefined.
    if (nav.webdriver === true) return true;

    var ua = typeof nav.userAgent === 'string' ? nav.userAgent : '';
    return BOT_UA_PATTERN.test(ua);
  }

  function generateAnonymousId() {
    try {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
      }
    } catch (error) {
      // crypto unavailable -- fall through to the timestamp-based id.
    }

    // Two random segments so the suffix is never empty even if one
    // Math.random() degenerates; the timestamp prefix keeps ids distinct
    // across milliseconds.
    var rand = (
      Math.random().toString(36).slice(2) +
      Math.random().toString(36).slice(2)
    ).slice(0, 12);
    return 'aid-' + Date.now().toString(36) + '-' + rand;
  }

  function getAnonymousId() {
    var storage;
    var existing;

    try {
      storage = window.localStorage;
      existing = storage.getItem(ANON_ID_STORAGE_KEY);
      if (existing) return existing;

      var generated = generateAnonymousId();
      storage.setItem(ANON_ID_STORAGE_KEY, generated);
      return generated;
    } catch (error) {
      // Storage blocked (private mode / pending cookie consent): fall back to a
      // per-pageload id so the event is still attributed to *a* user. Without a
      // user, Sentry reports 0 users-impacted on everything and real,
      // shopper-facing errors cannot be told apart from background noise.
      return generateAnonymousId();
    }
  }

  function buildSentryUser(cfg) {
    var customerId = cfg && cfg.customerId;

    // Logged-in shopper: a stable, non-PII id (the numeric Shopify customer id,
    // never an email). customer.id renders as JSON null when logged out, so a
    // simple null/empty check matches the only two values Liquid can emit.
    // Anonymous shopper: a persistent per-browser id.
    if (customerId != null && customerId !== '') {
      return { id: 'customer-' + customerId };
    }

    return { id: getAnonymousId() };
  }

  function buildIntegrations(replayEnabled, replayMaskAllText, replayBlockAllMedia) {
    var integrations = [];

    if (typeof window.Sentry.browserTracingIntegration === 'function') {
      integrations.push(window.Sentry.browserTracingIntegration());
    }

    if (replayEnabled && typeof window.Sentry.replayIntegration === 'function') {
      integrations.push(
        window.Sentry.replayIntegration({
          maskAllText: replayMaskAllText,
          blockAllMedia: replayBlockAllMedia
        })
      );
    }

    return integrations;
  }

  function getFrameSource(frame) {
    if (!frame || typeof frame !== 'object') return '';
    if (typeof frame.absPath === 'string' && frame.absPath.length > 0) return frame.absPath;
    if (typeof frame.filename === 'string' && frame.filename.length > 0) return frame.filename;
    return '';
  }

  function toAbsoluteFrameSource(source) {
    if (typeof source !== 'string' || source.length === 0) return '';
    if (/^https?:\/\//i.test(source)) return source;
    if (source.indexOf('//') === 0) return window.location.protocol + source;
    if (source.charAt(0) === '/') return window.location.origin + source;
    return '';
  }

  function parseFrameSource(source) {
    var absoluteSource = toAbsoluteFrameSource(source);

    if (!absoluteSource) return null;

    try {
      var parsed = new URL(absoluteSource);

      return {
        host: (parsed.hostname || '').toLowerCase(),
        path: parsed.pathname || ''
      };
    } catch (error) {
      return null;
    }
  }

  function isThemeAssetPath(path) {
    return typeof path === 'string' && THEME_ASSET_PATH_PATTERN.test(path);
  }

  function isAllowedInlinePath(path) {
    var i;

    if (typeof path !== 'string' || path.length === 0) return false;
    if (isThemeAssetPath(path)) return true;

    for (i = 0; i < NON_THEME_SAME_ORIGIN_PATH_PATTERNS.length; i++) {
      if (NON_THEME_SAME_ORIGIN_PATH_PATTERNS[i].test(path)) return false;
    }

    if (path.indexOf('/cdn/') === 0) return false;
    return true;
  }

  function getFrameSourceContext(frame) {
    var parts = [];

    if (!frame || typeof frame !== 'object') return '';
    if (typeof frame.context_line === 'string') parts.push(frame.context_line);
    if (Array.isArray(frame.pre_context)) parts = parts.concat(frame.pre_context);
    if (Array.isArray(frame.post_context)) parts = parts.concat(frame.post_context);

    return parts.join('\n');
  }

  function isShopifyInjectedFrame(frame) {
    // The ContextLines integration runs before beforeSend and populates source
    // context for same-origin inline scripts, letting us tell Shopify's injected
    // analytics bootstrap apart from genuine inline theme scripts (which share
    // the same page URL filename).
    var context = getFrameSourceContext(frame);
    var i;

    if (!context) return false;

    for (i = 0; i < SHOPIFY_INJECTED_SOURCE_TOKENS.length; i++) {
      if (context.indexOf(SHOPIFY_INJECTED_SOURCE_TOKENS[i]) !== -1) return true;
    }

    return false;
  }

  function isHtmlDocumentFrame(frame) {
    // The ContextLines integration populates the failing frame's source line
    // before beforeSend runs. When a <script> src resolved to an HTML document,
    // that line is the document's opening markup (e.g. "<!doctype html>"), so
    // the frame is third-party noise rather than theme-owned code.
    if (!frame || typeof frame !== 'object') return false;
    if (typeof frame.context_line !== 'string') return false;
    return HTML_DOCUMENT_SOURCE_PATTERN.test(frame.context_line);
  }

  function eventHasThemeOwnedFrame(event) {
    var exceptionValues = event && event.exception && event.exception.values;
    var currentHost = (window.location.hostname || '').toLowerCase();
    var i;
    var j;

    if (!Array.isArray(exceptionValues) || exceptionValues.length === 0) return false;

    for (i = 0; i < exceptionValues.length; i++) {
      var exceptionValue = exceptionValues[i] || {};
      var stacktrace = exceptionValue.stacktrace || {};
      var frames = stacktrace.frames;

      if (!Array.isArray(frames) || frames.length === 0) continue;

      for (j = 0; j < frames.length; j++) {
        var frameSource = getFrameSource(frames[j]);
        var parsedSource;

        if (!frameSource) continue;

        parsedSource = parseFrameSource(frameSource);
        if (!parsedSource) continue;

        if (isThemeAssetPath(parsedSource.path)) return true;
        if (parsedSource.host === currentHost && isAllowedInlinePath(parsedSource.path)) {
          // Same-origin inline scripts share the page URL as their filename, so
          // Shopify's platform-injected analytics bootstrap and in-app-browser
          // injected scripts that parse the page's own HTML would otherwise be
          // treated as theme-owned. Exclude them so their errors are dropped.
          if (isShopifyInjectedFrame(frames[j])) continue;
          if (isHtmlDocumentFrame(frames[j])) continue;
          return true;
        }
      }
    }

    return false;
  }

  // Some libraries reject with plain objects instead of real Error instances.
  // The Recharge storefront SDK, for example, throws values shaped like
  // { message, name: 'RechargeRequestError', status: 401 }. Sentry only builds
  // a typed, well-grouped issue from an actual Error -- a non-Error collapses
  // into the opaque "Object captured as exception with keys: ..." issue with no
  // usable stack and a generic title (SHOPIFY-11S). Coerce non-Errors into a
  // real Error that keeps the original name/message and carries the remaining
  // fields, so Sentry groups by the true error type (e.g. RechargeRequestError).
  function normalizeException(value) {
    if (value instanceof Error) return value;
    if (!value || typeof value !== 'object') return value;

    var message =
      typeof value.message === 'string' && value.message
        ? value.message
        : 'Non-Error exception captured';
    var normalized = new Error(message);

    if (typeof value.name === 'string' && value.name) {
      normalized.name = value.name;
    }

    // Carry the remaining own fields (status, code, ...) onto the Error so they
    // surface on the Sentry event. Skip message/stack (already handled) and
    // internal double-underscore flags (e.g. __sentry_captured__).
    Object.keys(value).forEach(function (key) {
      if (key === 'message' || key === 'stack' || key.indexOf('__') === 0) {
        return;
      }
      try {
        normalized[key] = value[key];
      } catch (assignError) {
        /* ignore read-only / non-assignable properties */
      }
    });

    return normalized;
  }

  function createObservability() {
    return freezeValue({
      captureException: function (error, context) {
        if (!window.Sentry || typeof window.Sentry.captureException !== 'function') return null;

        var normalizedError = normalizeException(error);

        if (
          context &&
          typeof context === 'object' &&
          typeof window.Sentry.withScope === 'function'
        ) {
          return window.Sentry.withScope(function (scope) {
            if (context.tags && typeof scope.setTags === 'function') {
              scope.setTags(context.tags);
            }
            if (context.extra && typeof scope.setExtras === 'function') {
              scope.setExtras(context.extra);
            }
            if (context.level && typeof scope.setLevel === 'function') {
              scope.setLevel(context.level);
            }
            return window.Sentry.captureException(normalizedError);
          });
        }

        return window.Sentry.captureException(normalizedError);
      },
      captureMessage: function (message, level) {
        if (!window.Sentry || typeof window.Sentry.captureMessage !== 'function') return null;
        return window.Sentry.captureMessage(message, level || 'info');
      },
      startSpan: function (spanContext, callback) {
        if (typeof callback !== 'function') return null;
        if (window.Sentry && typeof window.Sentry.startSpan === 'function') {
          return window.Sentry.startSpan(spanContext || { name: 'theme.operation' }, callback);
        }
        return callback(null);
      },
      startSpanManual: function (spanContext, callback) {
        if (typeof callback !== 'function') return null;
        if (window.Sentry && typeof window.Sentry.startSpanManual === 'function') {
          return window.Sentry.startSpanManual(
            spanContext || { name: 'theme.operation' },
            callback
          );
        }
        return callback(null);
      }
    });
  }

  function init() {
    var cfg = buildConfig();
    var active;
    var sampleRate;
    var tracesSampleRate;
    var replaysSessionSampleRate;
    var replaysOnErrorSampleRate;
    var replayEnabled;
    var replayMaskAllText;
    var replayBlockAllMedia;

    ensureObservability();

    if (state.initialized) return;

    // The Loader Script substitutes its OWN default Sentry.init() -- DSN only,
    // no release/denyUrls/ignoreErrors/beforeSend -- whenever sentryOnLoad
    // returns WITHOUT calling Sentry.init(). Returning early here (the old
    // bot/disabled short-circuit) therefore handed traffic to that unfiltered
    // default, which silently captured the bulk of events (no release, no
    // `surface` tag) and flooded the project. So we must ALWAYS call
    // Sentry.init().
    //
    // When Sentry should be inactive (automated traffic, or config disabled) we
    // still call init(), but with `enabled: false`. A disabled client is a true
    // no-op: it sends no error/replay/transaction AND starts no session, so the
    // crash-free SESSION rate is untouched (the original reason bots were
    // skipped) -- while the init() call still claims the slot and stops the
    // loader default from ever running.
    if (!window.Sentry || typeof window.Sentry.init !== 'function') return;

    active = parseBoolean(cfg.enabled, false) && Boolean(cfg.dsn) && !isLikelyBot();

    sampleRate = parseSampleRate(cfg.sampleRate, 1.0);
    tracesSampleRate = parseSampleRate(cfg.tracesSampleRate, 0.1);
    replaysSessionSampleRate = parseSampleRate(cfg.replaysSessionSampleRate, 0.05);
    replaysOnErrorSampleRate = parseSampleRate(cfg.replaysOnErrorSampleRate, 1.0);
    replayEnabled = parseBoolean(cfg.replayEnabled, true);
    replayMaskAllText = parseBoolean(cfg.replayMaskAllText, true);
    replayBlockAllMedia = parseBoolean(cfg.replayBlockAllMedia, true);

    if (!active || !replayEnabled) {
      replaysSessionSampleRate = 0;
      replaysOnErrorSampleRate = 0;
    }

    window.Sentry.init({
      dsn: cfg.dsn,
      enabled: active,
      environment: cfg.environment || 'production',
      release: cfg.release || undefined,
      // Attach the user from the very start via initialScope rather than a
      // post-init setUser(), so the Loader Script's buffered/early errors
      // (flushed at init time) are attributed to the shopper too.
      initialScope: active ? { user: buildSentryUser(cfg) } : undefined,
      sampleRate: active ? sampleRate : 0,
      tracesSampleRate: active ? tracesSampleRate : 0,
      replaysSessionSampleRate: replaysSessionSampleRate,
      replaysOnErrorSampleRate: replaysOnErrorSampleRate,
      tracePropagationTargets: ['localhost', /^\//],
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i,
        /\/cdn\/shopifycloud\/privacy-banner\/storefront-banner\.js/i,
        /\/cdn\/shopifycloud\/storefront\/assets\/shop_events_listener-[^/]+\.js/i,
        /\/web-pixels@[^/]+\//i,
        /(?:^|\/\/)js\.hs-analytics\.net\/analytics\//i
      ],
      ignoreErrors: [
        'jamToggleDumpStore',
        'fix-webm-duration',
        'The page keeping the extension port is moved into back/forward cache',
        'Script error.',
        // Dynamic ES-module import() failures, worded differently per engine
        // (Chrome/Chromium and Safari/WebKit below). Transient (flaky network,
        // content blockers) or from Shopify-platform-injected modules (e.g. shop-js
        // "Sign in with Shop" cart sync). The failing frame is an inline module
        // script whose filename is the page URL, so it leaks past the
        // same-origin-inline frame allowlist -- hence message-matching here.
        // SAFE ONLY because the theme ships no dynamic import()/type=module of its
        // own (verified). ignoreErrors runs in InboundFilters BEFORE the
        // theme-owned-frame allowlist, so if first-party code ever adopts a dynamic
        // import, NARROW OR REMOVE these patterns -- otherwise a genuine first-party
        // import failure would be silently dropped. (SHOPIFY-3DX/3DY)
        /^Failed to fetch dynamically imported module/i,
        /^Importing a module script failed/i,
        // iOS in-app browsers (Instagram, Facebook) inject a native-bridge
        // script that messages the host app through `window.webkit.messageHandlers`
        // -- the WKWebView message-handler API. When that bridge is absent the
        // injected `sendDataToNative` / `sendPageShowMessage` code throws
        // `TypeError: undefined is not an object (evaluating
        // 'window.webkit.messageHandlers')` on pageshow. Its frames carry the
        // same-origin page URL with no source line and the throwing frame is
        // anonymous, so the theme-owned-frame allowlist cannot classify it; but
        // no first-party theme code ever touches window.webkit, so matching the
        // accessor on the message reliably drops it (and catches the message-only
        // capture variants the frame allowlist never sees). (SHOPIFY-368)
        /webkit\.messageHandlers/i,
        // AccessiBe accessibility widget: its installer (injected by the accessiBe
        // Shopify app, NOT in the theme -- `acsbJS` appears nowhere in theme source)
        // calls `acsbJS.init(...)` in its script onload. When accessiBe's CDN app.js
        // loads abnormally (partial/blocked, mostly in-app browsers / WKWebView),
        // the onload runs before `acsbJS` is defined -> "ReferenceError: Can't find
        // variable: acsbJS" / "acsbJS is not defined". Its inline frame carries the
        // page URL so it leaks the theme-owned-frame allowlist. SAFE: acsbJS is
        // accessiBe-only, so matching the identifier cannot drop a theme error.
        // (SHOPIFY-H6/97/PB and siblings)
        /acsbJS/i
      ],
      beforeSend: function (event) {
        var hasExceptionValues = Boolean(
          event &&
          event.exception &&
          Array.isArray(event.exception.values) &&
          event.exception.values.length > 0
        );

        if (!hasExceptionValues) return event;
        if (eventHasThemeOwnedFrame(event)) return event;
        return null;
      },
      integrations: active
        ? buildIntegrations(replayEnabled, replayMaskAllText, replayBlockAllMedia)
        : []
    });

    // Only wire up tagging, user context, and the real observability facade when
    // Sentry is active. For inactive (bot/disabled) sessions the no-op
    // observability installed above stays in place and nothing is ever sent.
    if (active) {
      if (typeof window.Sentry.setTag === 'function') {
        window.Sentry.setTag('surface', 'shopify-theme');
      }

      window.etObservability = createObservability();
    }

    state.initialized = true;
  }

  ensureObservability();

  window.ET = window.ET || {};
  window.ET.Sentry = freezeValue({
    init: init,
    getConfig: function () {
      return state.config || buildConfig();
    }
  });

  window.sentryOnLoad = init;

  if (window.Sentry && typeof window.Sentry.init === 'function') {
    init();
  }
})();
